// apps/client/src/lib/socket.ts
//
// WebSocket client using Socket.io
// Socket.IO runs on the SAME server as the REST API (NestJS on Railway).
// Uses VITE_API_URL — the server's public HTTPS URL.
//

import { io, Socket } from 'socket.io-client';

// WebSocket URL — can point directly to Railway to bypass Cloudflare proxy.
// Falls back to VITE_API_URL (same server as REST API).
const WS_URL = import.meta.env['VITE_WS_URL']
    // @ts-ignore
    || import.meta.env['VITE_API_URL'];

interface ServerToClientEvents {
    player_joined: (data: { userId: string; roomCount: number; timestamp: string }) => void;
    player_left: (data: { userId?: string; roomCount: number; timestamp: string }) => void;
    player_count_updated: (data: { sessionId: string; playerCount: number; timestamp: string }) => void;
    number_drawn: (data: {
        number: number;
        drawnNumbers: number[];
        timestamp: string;
    }) => void;
    game_started: (data: { sessionId: string; timestamp: string }) => void;
    game_ended: (data: {
        sessionId: string;
        winners: { userId: string; prize: string }[];
        timestamp: string;
    }) => void;
    win: (data: {
        winType: 'LINE' | 'TWO_LINES' | 'BINGO';
        userId: string;
        timestamp: string;
    }) => void;
}

interface ClientToServerEvents {
    join_room: (
        payload: { sessionId: string; userId: string },
        callback: (response: { success: boolean; message: string }) => void
    ) => void;
    leave_room: (
        payload: { sessionId: string },
        callback: (response: { success: boolean; message: string }) => void
    ) => void;
}

class SocketClient {
    private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

    connect(): Socket<ServerToClientEvents, ClientToServerEvents> {
        if (this.socket?.connected) {
            return this.socket;
        }

        // If socket exists but disconnected, reconnect with fresh token
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        console.log('🔌 Connecting to WebSocket at:', WS_URL);

        // Read JWT from authStore for authenticated connection
        const authToken = this.getToken();

        const socketOpts: Record<string, unknown> = {
            transports: ['websocket', 'polling'],  // WebSocket first, polling fallback
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: Infinity,    // Never give up (server may restart)
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,       // Cap backoff at 10s
            timeout: 20000,                    // Allow more time for Railway cold starts
            path: '/socket.io/',               // Explicit path
        };

        if (authToken) {
            socketOpts['auth'] = { token: `Bearer ${authToken}` };
        } else {
            console.warn('⚠️ No auth token found — socket will be rejected by server');
        }

        this.socket = io(WS_URL, socketOpts as never);

        this.socket.on('connect', () => {
            console.log('✅ Socket connected:', this.socket?.id);
        });

        this.socket.on('disconnect', (reason) => {
            console.log('❌ Socket disconnected:', reason);
        });

        this.socket.on('connect_error', (error) => {
            console.error('❌ Socket connect error:', error.message);
        });

        // Handle auth errors from server (token invalid/expired)
        this.socket.on('auth_error' as any, (data: { message: string }) => {
            console.error('🔒 Auth error from server:', data.message);
        });

        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    /** Wait for socket to actually be connected (with timeout) */
    waitForConnection(timeoutMs = 10000): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.socket?.connected) {
                resolve(true);
                return;
            }
            if (!this.socket) {
                resolve(false);
                return;
            }

            const timer = setTimeout(() => {
                console.warn('⏱️ Socket connection timeout');
                resolve(false);
            }, timeoutMs);

            this.socket.once('connect', () => {
                clearTimeout(timer);
                resolve(true);
            });

            this.socket.once('connect_error', () => {
                clearTimeout(timer);
                resolve(false);
            });
        });
    }

    joinRoom(sessionId: string, userId: string): Promise<{ success: boolean; message: string }> {
        return new Promise((resolve) => {
            if (!this.socket) {
                resolve({ success: false, message: 'Socket not connected' });
                return;
            }

            this.socket.emit('join_room', { sessionId, userId }, (response) => {
                console.log('📥 Joined room:', response);
                resolve(response);
            });
        });
    }

    leaveRoom(sessionId: string): Promise<{ success: boolean; message: string }> {
        return new Promise((resolve) => {
            if (!this.socket) {
                resolve({ success: false, message: 'Socket not connected' });
                return;
            }

            this.socket.emit('leave_room', { sessionId }, (response) => {
                console.log('📤 Left room:', response);
                resolve(response);
            });
        });
    }

    onNumberDrawn(callback: ServerToClientEvents['number_drawn']) {
        this.socket?.on('number_drawn', callback);
    }

    onGameStarted(callback: ServerToClientEvents['game_started']) {
        this.socket?.on('game_started', callback);
    }

    onGameEnded(callback: ServerToClientEvents['game_ended']) {
        this.socket?.on('game_ended', callback);
    }

    onWin(callback: ServerToClientEvents['win']) {
        this.socket?.on('win', callback);
    }

    onPlayerJoined(callback: ServerToClientEvents['player_joined']) {
        this.socket?.on('player_joined', callback);
    }

    onPlayerLeft(callback: ServerToClientEvents['player_left']) {
        this.socket?.on('player_left', callback);
    }

    onPlayerCountUpdated(callback: ServerToClientEvents['player_count_updated']) {
        this.socket?.on('player_count_updated', callback);
    }

    removeAllListeners() {
        this.socket?.removeAllListeners();
    }

    getSocket() {
        return this.socket;
    }

    /** Read JWT from persisted auth store */
    private getToken(): string | undefined {
        try {
            const stored = localStorage.getItem('bingoton-auth');
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed?.state?.token;
            }
        } catch { /* ignore parse error */ }
        return undefined;
    }
}

export const socketClient = new SocketClient();
